/**
 * World events (data/worldEvents.ts, docs/systems/FUN_P1.md section 4): the clock (`world.time` in a 900 s cycle) starts and ends
 * an event every five minutes while the player is in the new Yanyu outdoors. The fair pays a visit to the market, the
 * raid sends three waves of bandits at two targets, the storm turns the weather. The schedule draws no dice; the
 * bandits' spawns go through the World's own paths. Event `worldEvent` tells the HUD (soon, start, wave, end).
 */
import {
  FAIR,
  RAID,
  STORM,
  WORLD_CYCLE,
  WORLD_EVENT_INFO,
  WORLD_EVENTS_ZONE,
  eventAt,
  lanternAt,
  nextEvent,
  type WorldEventId,
} from '../data/worldEvents';
import { floorAt, PX_METRES } from '../map';
import { coreOf, toCore } from '../region/core';
import type { WorldEvent } from '../world';
import { modesOf } from './modes';
import type { SystemHost, WorldSystem } from './types';

/** Event (part of `WorldEvent`). `n`: the wave, or the score at the end. */
export type WorldEventEvent = {
  type: 'worldEvent';
  id: WorldEventId;
  state: 'soon' | 'start' | 'wave' | 'end';
  n: number;
  win: boolean;
};

interface Raid {
  waves: number;
  hp: number[];
  score: number;
  leader: number | null;
  leaderDown: boolean;
  raiders: Set<number>;
  /** Killed raiders, cleared after `RAID.corpse` seconds so the monster timer never brings them back. */
  corpses: Map<number, number>;
  /** The mode the raid runs in (data/modes.ts `event:raid`). */
  mode: 'npc' | 'pvp';
}

interface Stranded {
  id: number;
  x: number;
  y: number;
  hold: number;
  breath: number;
}

interface Storm {
  people: Stranded[];
  saved: number;
  byPlayer: number;
  mode: 'npc' | 'pvp';
  crewT: number;
}

export class WorldEventsSystem implements WorldSystem {
  readonly name = 'worldEvents';
  /** The cycle count and event under way (so an event starts once). */
  private active: { cycle: number; id: WorldEventId; joined: boolean; buns: Set<string>; points: number; lanterns: Set<number> } | null = null;
  private warned = -1;
  private raid: Raid | null = null;
  private storm: Storm | null = null;
  /** The raid's clock (seconds into it), for the corpses' timers. */
  private raidT = 0;

  constructor(private readonly h: SystemHost) {}

  /** The event on now: id, seconds in, and the raid's targets' health when it is the raid. */
  current(): { id: WorldEventId; t: number; hp?: readonly number[]; score?: number } | null {
    const w = this.h.world;
    if (!this.active) return null;
    const e = eventAt(w.time % WORLD_CYCLE.length);
    return e ? { ...e, ...(this.raid ? { hp: this.raid.hp, score: this.raid.score } : {}) } : null;
  }

  private send(id: WorldEventId, state: WorldEventEvent['state'], n = 0, win = false): void {
    this.h.emit({ type: 'worldEvent', id, state, n, win });
  }

  step(dt: number): void {
    const w = this.h.world;
    const p = w.player;
    if (w.map.zone !== WORLD_EVENTS_ZONE || p.dead || w.interiors.inside) {
      if (this.active) this.end(false);
      return;
    }
    const phase = w.time % WORLD_CYCLE.length;
    const cycle = Math.floor(w.time / WORLD_CYCLE.length);
    const here = eventAt(phase);
    // The warning, once per event.
    const nx = nextEvent(phase);
    const key = cycle * 10 + WORLD_EVENT_INFO_ORDER.indexOf(nx.id);
    if (!here && nx.in <= WORLD_CYCLE.warn && this.warned !== key) {
      this.warned = key;
      this.send(nx.id, 'soon', Math.ceil(nx.in));
      this.h.notice(`อีกไม่นาน ${WORLD_EVENT_INFO[nx.id].name}`);
    }
    if (this.active && (!here || here.id !== this.active.id || this.active.cycle !== cycle)) this.end(true);
    if (here && !this.active) {
      this.active = { cycle, id: here.id, joined: false, buns: new Set(), points: 0, lanterns: new Set() };
      this.send(here.id, 'start');
      this.h.notice(`เริ่ม ${WORLD_EVENT_INFO[here.id].name}`);
      if (here.id === 'raid') {
        const mode = modesOf(w)?.resolve('event:raid') ?? 'npc';
        this.raid = { waves: 0, hp: RAID.targets.map(() => RAID.targetHp), score: 0, leader: null, leaderDown: false, raiders: new Set(), corpses: new Map(), mode };
        if (mode === 'npc') this.h.notice('ยามเมืองสี่นายช่วยป้องกัน');
      }
      if (here.id === 'storm') this.startStorm();
      if (here.id === 'storm') Object.assign(w.weather, { from: w.weather.kind, kind: 'storm', blend: w.weather.kind === 'storm' ? 1 : 0, left: Math.max(w.weather.left, WORLD_CYCLE.run - here.t) });
    }
    if (!here || !this.active) return;
    // The spots of the data are in the town's own metres; in a region the town sits at the core's origin.
    const lp = toCore(w.map, p.x, p.y);
    if (here.id === 'fair' && !this.active.joined && FAIR.areas.some(([x0, x1, y0, y1]) => lp.x >= x0 && lp.x < x1 && lp.y >= y0 && lp.y < y1)) {
      this.active.joined = true;
      this.h.grantReward({ cultivation: FAIR.joinCultivation });
      this.h.notice(`ร่วมงาน ${WORLD_EVENT_INFO.fair.name} +${FAIR.joinCultivation} แต้มฝึกปรือ`);
    }
    if (here.id === 'fair') {
      this.stepBuns();
      this.stepLanterns(here.t);
    }
    if (here.id === 'raid' && this.raid) this.stepRaid(here.t, dt);
    if (here.id === 'storm' && this.storm) this.stepStorm(dt);
  }

  /** ชิงซาลาเปา: a bun is taken by standing on it (near its ring spot, at its ring's height). */
  private stepBuns(): void {
    const a = this.active!;
    const w = this.h.world;
    const p = w.player;
    const T = FAIR.tower;
    const lp = toCore(w.map, p.x, p.y);
    if (Math.hypot(lp.x - T.x, lp.y - T.y) > T.radius + T.reach) return;
    const alt = (floorAt(w.map, p.x, p.y) + p.z) * PX_METRES;
    T.rings.forEach((ring, ri) => {
      if (Math.abs(alt - ring.h) > T.band) return;
      for (let k = 0; k < T.buns; k++) {
        const key = `${ri}.${k}`;
        if (a.buns.has(key)) continue;
        const ang = (k / T.buns) * Math.PI * 2;
        if (Math.hypot(lp.x - (T.x + Math.cos(ang) * T.radius), lp.y - (T.y + Math.sin(ang) * T.radius)) > T.reach) continue;
        a.buns.add(key);
        a.points += ring.points;
        this.h.grantReward({ gold: ring.points * T.goldPerPoint });
        this.h.notice(`ซาลาเปา +${ring.points} คะแนน (รวม ${a.points})`);
      }
    });
  }

  /** ร่อนโคมลอย: touching a drifting lantern in the air (off the floor) pays, once per lantern per fair. */
  private stepLanterns(t: number): void {
    const a = this.active!;
    const w = this.h.world;
    const p = w.player;
    if (p.z <= 0) return;
    const L = FAIR.lanterns;
    const lp = toCore(w.map, p.x, p.y);
    const alt = (floorAt(w.map, p.x, p.y) + p.z) * PX_METRES;
    for (let k = 0; k < L.count; k++) {
      if (a.lanterns.has(k)) continue;
      const l = lanternAt(k, t);
      if (Math.hypot(lp.x - l.x, lp.y - l.y) > L.reach || Math.abs(alt - l.h) > L.band) continue;
      a.lanterns.add(k);
      this.h.grantReward({ gold: L.gold });
      this.h.emit({ type: 'secret', id: `lantern.${k}`, kind: 'lantern', refill: false, gold: L.gold, cultivation: 0 });
      this.h.notice(`แตะโคมลอย +${L.gold} ตำลึง (${a.lanterns.size}/${L.count})`);
    }
  }

  private startStorm(): void {
    const mode = modesOf(this.h.world)?.resolve('event:storm') ?? 'npc';
    const o = coreOf(this.h.world.map);
    const people = STORM.stranded.map((s0) => {
      const s = { ...s0, x: s0.x + o.x, y: s0.y + o.y };
      const e = this.h.add({ kind: 'npc', x: s.x, y: s.y, sprite: 'fisherOld', name: 'ชาวประมงติดพายุ', title: s.name, facing: 1 });
      return { id: e.id, x: s.x, y: s.y, hold: 0, breath: STORM.breath };
    });
    this.storm = { people, saved: 0, byPlayer: 0, mode, crewT: 0 };
    this.h.notice('ชาวประมงหกคนติดพายุ ไปช่วยพวกเขา');
  }

  /** Standing near a stranded fisherman for `STORM.hold` seconds saves him; his breath runs out; the crew saves too. */
  private stepStorm(dt: number): void {
    const s = this.storm!;
    const w = this.h.world;
    const p = w.player;
    for (const f of [...s.people]) {
      f.breath -= dt;
      if (Math.hypot(p.x - f.x, p.y - f.y) <= STORM.reach) f.hold += dt;
      else f.hold = 0;
      if (f.hold >= STORM.hold) {
        this.save(f, true);
      } else if (f.breath <= 0) this.lose(f, 'ถูกคลื่นพัดไป');
    }
    if (s.mode === 'npc' && s.people.length) {
      s.crewT += dt;
      if (s.crewT >= STORM.crewEvery) {
        s.crewT = 0;
        this.save(s.people[0]!, false);
      }
    }
  }

  private save(f: Stranded, byPlayer: boolean): void {
    const s = this.storm!;
    s.people = s.people.filter((x) => x !== f);
    this.h.world.entities.delete(f.id);
    s.saved++;
    if (byPlayer) {
      s.byPlayer++;
      this.h.grantReward({ gold: STORM.gold, cultivation: STORM.cultivation });
      this.h.notice(`ช่วยชาวประมงได้ ${s.byPlayer} · +${STORM.gold} ตำลึง`);
    } else this.h.notice('ลูกเรือท่าเรือช่วยชาวประมงไปคนหนึ่ง');
  }

  private lose(f: Stranded, why: string): void {
    const s = this.storm!;
    s.people = s.people.filter((x) => x !== f);
    this.h.world.entities.delete(f.id);
    this.h.notice(`ชาวประมงคนหนึ่ง${why}`);
  }

  private stepRaid(t: number, dt: number): void {
    this.raidT = t;
    const r = this.raid!;
    const w = this.h.world;
    const o = coreOf(w.map);
    while (r.waves < RAID.waves.length && t >= RAID.waves[r.waves]!.at) {
      const wave = RAID.waves[r.waves]!;
      r.waves++;
      for (let i = 0; i < wave.bandits; i++) {
        const tg = RAID.targets[i % RAID.targets.length]!;
        const e = this.h.spawnMonster('bandit', o.x + RAID.from[0] + (i % 3) * 1.2, o.y + RAID.from[1] + Math.floor(i / 3) * 1.2);
        // Leashed to its target: the bandit's own going-home walks it there.
        if (e.brain) Object.assign(e.brain, { spawnX: tg.x + o.x, spawnY: tg.y + o.y });
        r.raiders.add(e.id);
      }
      if (wave.leader) {
        const e = this.h.spawnMonster('bandit', o.x + RAID.from[0], o.y + RAID.from[1] + 3);
        Object.assign(e, { name: RAID.leaderName, hp: e.maxHp * RAID.leaderHpMul, maxHp: e.maxHp * RAID.leaderHpMul, level: 8, scale: 1.2 });
        if (e.brain) Object.assign(e.brain, { spawnX: RAID.targets[0]!.x + o.x, spawnY: RAID.targets[0]!.y + o.y });
        r.leader = e.id;
        r.raiders.add(e.id);
      }
      this.send('raid', 'wave', r.waves);
      this.h.notice(`โจรระลอก ${r.waves}`);
    }
    // A killed raider's body goes after a few seconds; the monster timer never gets to bring it back.
    for (const [id, since] of r.corpses) {
      if (t - since >= RAID.corpse) {
        w.entities.delete(id);
        r.corpses.delete(id);
      }
    }
    const mul = r.mode === 'npc' ? RAID.watchmenMul : 1;
    const o2 = coreOf(w.map);
    // Bandits that reach a target take its health.
    for (const id of r.raiders) {
      const e = w.get(id);
      if (!e || e.dead) continue;
      RAID.targets.forEach((tg, i) => {
        if (r.hp[i]! > 0 && Math.hypot(e.x - o2.x - tg.x, e.y - o2.y - tg.y) <= RAID.reach) r.hp[i] = Math.max(0, r.hp[i]! - RAID.drain * mul * dt);
      });
    }
    if (r.hp.every((v) => v <= 0)) this.end(true);
  }

  observe(e: WorldEvent): void {
    const r = this.raid;
    if (!r || e.type !== 'death' || !r.raiders.has(e.id)) return;
    r.raiders.delete(e.id);
    r.corpses.set(e.id, this.raidT);
    if (e.id === r.leader) {
      r.leaderDown = true;
      r.score += RAID.score.leader;
    } else r.score += RAID.score.kill;
    if (r.leaderDown) this.end(true);
  }

  /** Closes the event under way (`pay`: the raid's rewards; false when the player left). */
  private end(pay: boolean): void {
    const a = this.active;
    if (!a) return;
    this.active = null;
    let win = false;
    let n = 0;
    if (a.id === 'fair') {
      n = a.points;
      // The top scorer against the NPC climbers (the pvp mode compares the zone's players on the server).
      win = a.points >= FAIR.top.points;
      if (pay && win) {
        this.h.grantReward({ gold: FAIR.top.gold, title: FAIR.top.title });
        this.h.notice(`ยอดนักชิงซาลาเปา +${FAIR.top.gold} ตำลึง`);
      }
    }
    if (a.id === 'raid' && this.raid) {
      const r = this.raid;
      win = r.leaderDown || r.hp.some((v) => v > 0);
      n = r.score;
      const tier = RAID.tiers.find((x) => r.score >= x[0]);
      if (pay && tier) {
        const mul = win ? RAID.winMul : 1;
        this.h.grantReward({ gold: Math.round(tier[1] * mul), cultivation: Math.round(tier[2] * mul) });
        this.h.notice(`${WORLD_EVENT_INFO.raid.name} คะแนน ${r.score}${win ? ' · ชนะ' : ''}`);
      }
      // The bandits that are left go home.
      for (const id of [...r.raiders, ...r.corpses.keys()]) this.h.world.entities.delete(id);
      this.raid = null;
    }
    if (a.id === 'storm' && this.storm) {
      const s = this.storm;
      for (const f of s.people) this.h.world.entities.delete(f.id);
      n = s.byPlayer;
      win = s.byPlayer === STORM.stranded.length;
      if (pay && win) {
        this.h.grantReward({ gold: STORM.allBonusGold });
        this.h.notice(`ช่วยครบหกคน +${STORM.allBonusGold} ตำลึง`);
      }
      this.storm = null;
    }
    this.send(a.id, 'end', n, win);
  }
}

const WORLD_EVENT_INFO_ORDER: readonly WorldEventId[] = ['fair', 'raid', 'storm'];
